import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getAllStates, getStateData } from '@/lib/stateData';
import SEO from '@/components/seo/SEO';
import { Map, ArrowRight, Search, ExternalLink, Phone, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { trackExternalHandoff } from '@/lib/tracking';

const StateIndexPage: React.FC = () => {
  const [filter, setFilter] = useState('');
  
  // Memoize the hydration of state data to avoid recalculating on every render
  const richStates = useMemo(() => {
    return getAllStates().map(basicState => {
        return getStateData(basicState.slug);
    });
  }, []);

  const filteredStates = richStates.filter(state => 
    state.name.toLowerCase().includes(filter.toLowerCase()) || 
    state.abbreviation.toLowerCase().includes(filter.toLowerCase()) ||
    state.authorityName.toLowerCase().includes(filter.toLowerCase())
  );

  // Generate ItemList Schema for Google Rich Results
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": filteredStates.map((state, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "url": `https://runofftrack.com/swppp-requirements/${state.slug}`,
      "name": `${state.name} SWPPP Requirements`
    }))
  };

  const handleNoiClick = (url: string, stateName: string) => {
      trackExternalHandoff(url, 'noi_portal');
  };

  return (
    <div className="page-shell">
      <SEO 
        title="US State SWPPP Requirements & NOI Filing Directory"
        description="Direct links to file Notices of Intent (NOI) for stormwater permits in all 50 states. Find your local environmental agency contact info."
        canonical="/swppp-requirements"
        jsonLd={jsonLd}
      />

      <div className="page-intro">
        <div className="container relative z-10 mx-auto max-w-4xl px-4 text-center">
           <Breadcrumbs items={[
               { label: 'Resources', href: '/' },
               { label: 'State Directory', href: '/swppp-requirements' }
           ]} />
           
           <div className="eyebrow mb-6">
             <Map size={15} /> Field directory
           </div>
           <h1 className="mb-6 text-4xl font-black tracking-[-0.04em] md:text-6xl">National Compliance Directory</h1>
           <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
             Direct access to <strong>Notice of Intent (NOI)</strong> portals, state-specific SWPPP checklists, and agency contacts for all 50 states.
           </p>

           <div className="relative max-w-lg mx-auto">
             <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5" />
             <Input aria-label="Search states"
                placeholder="Search by state or agency (e.g. 'Texas' or 'TCEQ')..." 
                className="h-14 rounded-xl border-primary/20 bg-background pl-12 text-base shadow-sm focus-visible:ring-primary"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
             />
           </div>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-7xl mx-auto">
        {filteredStates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredStates.map((state) => (
                    <Card key={state.slug} className="group relative flex flex-col transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg">
                        <CardHeader className="pb-3">
                            <div className="flex justify-between items-start">
                                <CardTitle className="text-xl group-hover:text-primary transition-colors">
                                    <Link to={`/swppp-requirements/${state.slug}`} className="absolute inset-0 z-0">
                                        <span className="sr-only">View {state.name} Guide</span>
                                    </Link>
                                    {state.name}
                                </CardTitle>
                                <Badge variant="outline" className="font-mono text-xs">{state.abbreviation}</Badge>
                            </div>
                            <div className="text-sm text-muted-foreground font-medium">{state.authorityName}</div>
                        </CardHeader>
                        
                        <CardContent className="flex-grow space-y-4 relative z-10 pointer-events-none">
                            <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                                <FileText size={16} className="mt-0.5 text-primary/70"/>
                                <span className="line-clamp-2">{state.permitName}</span>
                            </div>
                            
                            {state.agencyContact?.phone && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Phone size={14} />
                                    <span>Agency: {state.agencyContact.phone}</span>
                                </div>
                            )}
                        </CardContent>

                        <CardFooter className="pt-0 relative z-20 gap-2">
                            <Button size="sm" className="w-full gap-2 bg-primary/5 text-primary hover:bg-primary hover:text-primary-foreground border border-primary/10" asChild>
                                <Link to={`/swppp-requirements/${state.slug}`}>
                                    Requirements <ArrowRight size={14} />
                                </Link>
                            </Button>
                            
                            {state.noiLink && (
                                <Button size="sm" variant="outline" className="w-full gap-2" asChild onClick={() => handleNoiClick(state.noiLink!, state.name)}>
                                    <a href={state.noiLink} target="_blank" rel="noopener noreferrer">
                                        File NOI <ExternalLink size={14} />
                                    </a>
                                </Button>
                            )}
                        </CardFooter>
                    </Card>
                ))}
            </div>
        ) : (
            <div className="text-center py-32 bg-muted/10 rounded-3xl border-2 border-dashed border-border">
                <Map className="mx-auto h-12 w-12 text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-semibold">No states found</h3>
                <p className="text-muted-foreground">Try searching for a different state name or abbreviation.</p>
                <Button variant="link" onClick={() => setFilter('')} className="mt-2">Clear Search</Button>
            </div>
        )}
      </div>
    </div>
  );
};

export default StateIndexPage;
